import AppConfig from '../AppConfig';
import * as Constant from '../Constants/Constant';
import * as Messages from '../Resources/Messages';
import { getAllowedSetForUser } from '../Menu/menuConfig';
import UserInformation from '../Models/ViewModels/UserInformation';

/** Server: `BadRequest(string)` → JSON `{ "Message": ... }`。本文は `"Failed to download files. " + Join("file: reason"," / ")`. */
const GEN1_ZIP_ERR_PREFIX_FAILED_FILES = 'Failed to download files.';

function extractFailedGen1LicenseKeyFileNamesFromZipErrorBody(rawBody) {
    if (rawBody == null || typeof rawBody !== 'string') return [];

    let message = rawBody.trim();
    try {
        const parsed = JSON.parse(message);
        if (parsed && typeof parsed.Message === 'string') {
            message = parsed.Message.trim();
        } else if (parsed && typeof parsed.message === 'string') {
            message = parsed.message.trim();
        }
    } catch {
        /* Plain text response */
    }

    if (!message.startsWith(GEN1_ZIP_ERR_PREFIX_FAILED_FILES)) return [];

    const tail = message.slice(GEN1_ZIP_ERR_PREFIX_FAILED_FILES.length).trim();
    if (!tail) return [];

    const names = [];
    const seen = new Set();
    for (const segment of tail.split(' / ')) {
        const seg = segment.trim();
        if (!seg) continue;
        const colonIx = seg.indexOf(': ');
        /** Server uses `$"{FileName}: {reason}"`; empty FileName yields ": reason". */
        let name;
        if (colonIx < 0) name = seg;
        else if (colonIx === 0) name = '(unnamed file)';
        else name = seg.slice(0, colonIx).trim();
        name = String(name ?? '').trim();
        if (!name || seen.has(name)) continue;
        seen.add(name);
        names.push(name);
    }

    return names;
}

/** Response header from partial-failure ZIP: Base64-encoded UTF-8 JSON string[]. */
function parsePartialFailedLicenseKeyFileNamesFromZipResponse(response) {
    if (!response || typeof response.headers?.get !== 'function') return [];
    const raw = response.headers.get('x-gen1-licensekey-failed-files');
    if (!raw || typeof raw !== 'string') return [];
    try {
        const trimmed = raw.trim();
        if (typeof atob !== 'function') return [];
        const binary = atob(trimmed);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) {
            bytes[i] = binary.charCodeAt(i) & 0xff;
        }
        let jsonStr;
        if (typeof TextDecoder !== 'undefined') {
            jsonStr = new TextDecoder('utf-8').decode(bytes);
        } else {
            jsonStr = [...bytes].map((b) => String.fromCharCode(b)).join('');
        }
        const arr = JSON.parse(jsonStr);
        return Array.isArray(arr)
            ? arr.map((x) => String(x ?? '').trim()).filter(Boolean)
            : [];
    } catch {
        return [];
    }
}

export default {
    /**
     * サーバーが MENU_PERMISSIONS_CHANGED を返したとき、セッション上は当該タブを
     * 保持していた場合のみ Gen1 ブロック UI を出す。静的権限不足は通常エラー表示。
     */
    gen1MenuPermTryBlock(message, requiredSessionTab) {
        const m = typeof message === 'string' ? message.trim() : '';
        if (m !== Messages.REPAIRREQUEST_MSG_MENU_PERMISSIONS_CHANGED)
            return false;

        if (requiredSessionTab) {
            const ui = this._userInformation
                ? new UserInformation(this._userInformation)
                : null;
            if (!ui || !getAllowedSetForUser(ui).has(requiredSessionTab)) {
                return false;
            }
        }

        this.Dispatch({
            type: Constant.GEN1_ACT_MENU_PERM_BLOCKED,
            message: Messages.REPAIRREQUEST_MSG_MENU_PERMISSIONS_BLOCK_BODY,
        });
        return true;
    },

    /** Gen1MasterItem を取得。Gen1 画面表示時に呼ぶ。 */
    fetchGen1Masters(csrftoken) {
        this.gen1MasterFetchError(false);
        this.gen1MasterFetching(true);
        fetch(AppConfig.ApiUrlGen1 + '/masterData', {
            mode: 'cors',
            credentials: 'include',
            headers: { csrftoken: JSON.stringify(csrftoken) },
        })
            .then((response) => {
                if (!response.ok) {
                    return response
                        .json()
                        .then((err) => {
                            throw new Error(
                                err.Message ??
                                    err.message ??
                                    'Failed to fetch Gen1 masters.'
                            );
                        })
                        .catch((parseErr) => {
                            if (parseErr instanceof Error) throw parseErr;
                            throw new Error(
                                `Gen1 masters fetch failed (${response.status}).`
                            );
                        });
                }
                return response.json();
            })
            .then((data) => {
                this.gen1MasterFetchSuccess(
                    data.manufactureRepMaster ?? [],
                    data.modelMasterForGen1 ?? []
                );
                this.gen1MasterFetching(false);
                this.gen1MasterFetchError(false);
            })
            .catch((e) => {
                if (
                    !this.gen1MenuPermTryBlock(
                        e?.message,
                        Constant.REPAIRREQUEST_STAT_GEN1_APPLICATION
                    )
                ) {
                    this.gen1MasterFetchError(true);
                }
                this.gen1MasterFetching(false);
            });
    },

    /**
     * OrderSerialMaster を SO# で検索（Gen1 Application の Search 用）。
     * excludeHeaderId を渡すと編集中注文の履歴をサーバ側で除外し、再 Search 時の自己衝突を防ぐ。
     */
    fetchOrderSerialsByOrderNumber(csrftoken, orderNumber, excludeHeaderId) {
        const q = (orderNumber ?? '').trim();
        const headerId = Number(excludeHeaderId) || 0;
        let url =
            AppConfig.ApiUrlGen1 +
            '/orderSerials?orderNumber=' +
            encodeURIComponent(q);
        if (headerId >= 1) {
            url += '&excludeHeaderId=' + encodeURIComponent(String(headerId));
        }

        return fetch(url, {
            mode: 'cors',
            credentials: 'include',
            headers: { csrftoken: JSON.stringify(csrftoken) },
        }).then((response) => {
            if (!response.ok) {
                return response
                    .json()
                    .then((err) => {
                        const msg =
                            err.Message ??
                            err.message ??
                            'Failed to fetch Order Serial data.';
                        if (
                            this.gen1MenuPermTryBlock(
                                msg,
                                Constant.REPAIRREQUEST_STAT_GEN1_APPLICATION
                            )
                        ) {
                            return Promise.reject(new Error(msg));
                        }
                        throw new Error(msg);
                    })
                    .catch((parseErr) => {
                        if (parseErr instanceof Error) throw parseErr;
                        throw new Error(
                            `Order Serial fetch failed (${response.status}).`
                        );
                    });
            }
            return response
                .json()
                .then((data) => (Array.isArray(data) ? data : []));
        });
    },
    gen1MasterFetching(bool) {
        this.Dispatch({
            type: Constant.GEN1_ACT_MASTERFETCH_FETCHING,
            bool,
        });
    },
    gen1MasterFetchError(bool) {
        this.Dispatch({
            type: Constant.GEN1_ACT_MASTERFETCH_FETCHINGERROR,
            bool,
        });
    },
    gen1MasterFetchSuccess(manufactureRepMaster, modelMasterForGen1) {
        this.Dispatch({
            type: Constant.GEN1_ACT_MASTERFETCH_SUCCESS,
            manufactureRepMaster,
            modelMasterForGen1,
        });
    },

    gen1OrdersListFetching(bool) {
        this.Dispatch({
            type: Constant.GEN1_ACT_ORDERSLIST_FETCHING,
            bool,
        });
    },

    gen1OrdersListFetchError(bool, message = '') {
        this.Dispatch({
            type: Constant.GEN1_ACT_ORDERSLIST_FETCHINGERROR,
            bool,
            message,
        });
    },

    gen1OrdersListSuccess(gen1Orders, totalCount = 0) {
        this.Dispatch({
            type: Constant.GEN1_ACT_ORDERSLIST_SUCCESS,
            gen1Orders,
            totalCount,
        });
    },

    /** ログイン顧客の Gen1 注文を DB から取得（履歴画面表示時/絞り込み時） */
    fetchGen1Orders(csrftoken, filters = {}, page = 1, pageSize = 20) {
        this.gen1OrdersListFetchError(false, '');
        this.gen1OrdersListFetching(true);

        const localDateYmdToUtcIsoStart = (dateYmd) => {
            if (!dateYmd || String(dateYmd).trim() === '') return null;
            const d = new Date(`${String(dateYmd).trim()}T00:00:00`);
            return Number.isNaN(d.getTime()) ? null : d.toISOString();
        };

        const localDateYmdToUtcIsoExclusiveEnd = (dateYmd) => {
            if (!dateYmd || String(dateYmd).trim() === '') return null;
            const d = new Date(`${String(dateYmd).trim()}T00:00:00`);
            if (Number.isNaN(d.getTime())) return null;
            d.setDate(d.getDate() + 1);
            return d.toISOString();
        };

        const queryParams = new URLSearchParams();
        [
            'status',
            'contactName',
            'trunkingOrderNumber',
            'dealerOrderNumber',
            'orderNumber',
            'systemId',
        ].forEach((key) => {
            const value = filters?.[key];
            if (value != null && String(value).trim() !== '') {
                queryParams.set(key, String(value).trim());
            }
        });

        // RequestedDate range filter (UTC) - align with browser-local date pickers
        const fromUtc = localDateYmdToUtcIsoStart(filters?.requestedDateFrom);
        const toUtc = localDateYmdToUtcIsoExclusiveEnd(
            filters?.requestedDateTo
        );
        if (fromUtc) queryParams.set('requestedDateFromUtc', fromUtc);
        if (toUtc) queryParams.set('requestedDateToUtc', toUtc);

        queryParams.set('page', String(page));
        queryParams.set('pageSize', String(pageSize));

        const requestUrl =
            queryParams.toString() === ''
                ? `${AppConfig.ApiUrlGen1}/orders`
                : `${AppConfig.ApiUrlGen1}/orders?${queryParams.toString()}`;

        fetch(requestUrl, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: { csrftoken: JSON.stringify(csrftoken) },
        })
            .then((response) => {
                if (!response.ok) {
                    return response
                        .json()
                        .then((err) => {
                            const msg =
                                err.Message ??
                                err.message ??
                                'Failed to fetch Gen1 orders.';
                            throw new Error(msg);
                        })
                        .catch((parseErr) => {
                            if (parseErr instanceof Error) throw parseErr;
                            throw new Error(
                                `Gen1 orders fetch failed (${response.status}).`
                            );
                        });
                }
                return response.json();
            })
            .then((data) => {
                const rawItems = Array.isArray(data)
                    ? data
                    : (data?.items ?? data?.orders ?? []);
                const totalCount = Array.isArray(data)
                    ? rawItems.length
                    : Number(data?.totalCount ?? rawItems.length);
                this.gen1OrdersListSuccess(rawItems, totalCount);
                this.gen1OrdersListFetching(false);
                this.gen1OrdersListFetchError(false, '');
            })
            .catch((e) => {
                if (
                    !this.gen1MenuPermTryBlock(
                        e?.message,
                        Constant.REPAIRREQUEST_STAT_GEN1_HISTORY
                    )
                ) {
                    this.gen1OrdersListFetchError(
                        true,
                        e?.message ?? 'An error occurred while loading orders.'
                    );
                }
                this.gen1OrdersListFetching(false);
            });
    },

    /** 注文 ID で Gen1 注文の詳細を DB から取得（履歴の詳細画面用。結果は Promise で返す） */
    fetchGen1OrderById(csrftoken, orderId) {
        const idNum = Number(orderId);
        if (
            orderId == null ||
            orderId === '' ||
            Number.isNaN(idNum) ||
            idNum < 1
        ) {
            return Promise.reject(new Error('Invalid order id.'));
        }

        const url = `${AppConfig.ApiUrlGen1}/${idNum}`;
        return fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: { csrftoken: JSON.stringify(csrftoken) },
        }).then((response) => {
            if (response.status === 404) {
                throw new Error('Order not found.');
            }
            if (!response.ok) {
                return response
                    .json()
                    .then((err) => {
                        const msg =
                            err.Message ??
                            err.message ??
                            'Failed to fetch order.';
                        this.gen1MenuPermTryBlock(
                            msg,
                            Constant.REPAIRREQUEST_STAT_GEN1_HISTORY
                        );
                        throw new Error(msg);
                    })
                    .catch((parseErr) => {
                        if (parseErr instanceof Error) throw parseErr;
                        throw new Error(
                            `Order fetch failed (${response.status}).`
                        );
                    });
            }
            return response.json();
        });
    },

    /** License Key Files をZIPでダウンロード（Completedのみ） */
    downloadGen1LicenseKeyFilesZip(csrftoken, orderId, fileIds = []) {
        const idNum = Number(orderId);
        if (
            orderId == null ||
            orderId === '' ||
            Number.isNaN(idNum) ||
            idNum < 1
        ) {
            return Promise.reject(new Error('Invalid order id.'));
        }
        const ids = Array.isArray(fileIds)
            ? fileIds
                  .map((x) => Number(x))
                  .filter((x) => Number.isFinite(x) && x > 0)
            : [];
        if (ids.length === 0) {
            return Promise.reject(new Error('No files selected.'));
        }

        const url = `${AppConfig.ApiUrlGen1}/${idNum}/licenseKey/downloadZip`;
        return fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                Accept: 'application/zip',
                'Content-Type': 'application/json',
                csrftoken: JSON.stringify(csrftoken),
            },
            body: JSON.stringify({ fileIds: ids }),
        }).then(async (response) => {
            if (!response.ok) {
                const raw = await response.text().catch(() => '');
                let menuErrText;
                try {
                    const err = JSON.parse(raw);
                    const m = err?.Message ?? err?.message;
                    if (typeof m === 'string') menuErrText = m.trim();
                } catch {
                    /* non-JSON body */
                }
                if (
                    menuErrText &&
                    this.gen1MenuPermTryBlock(
                        menuErrText,
                        Constant.REPAIRREQUEST_STAT_GEN1_HISTORY
                    )
                ) {
                    throw new Error(menuErrText);
                }
                const fileNames =
                    extractFailedGen1LicenseKeyFileNamesFromZipErrorBody(raw);
                const userMessage =
                    fileNames.length > 0
                        ? `Failed to download license key file(s): ${fileNames.join(
                              ', '
                          )}.`
                        : 'Failed to download license key files.';
                throw new Error(userMessage);
            }
            const partialFailedNames =
                parsePartialFailedLicenseKeyFileNamesFromZipResponse(response);
            const blob = await response.blob();
            return {
                blob,
                partialFailedLicenseKeyFileNames: partialFailedNames,
            };
        });
    },

    /** 注文 ID で Gen1 注文を物理削除（Draft のみ。結果は Promise で返す） */
    deleteGen1OrderById(csrftoken, orderId) {
        const idNum = Number(orderId);
        if (
            orderId == null ||
            orderId === '' ||
            Number.isNaN(idNum) ||
            idNum < 1
        ) {
            return Promise.reject(new Error('Invalid order id.'));
        }

        const url = `${AppConfig.ApiUrlGen1}/${idNum}`;
        return fetch(url, {
            method: 'DELETE',
            mode: 'cors',
            credentials: 'include',
            headers: { csrftoken: JSON.stringify(csrftoken) },
        }).then((response) => {
            if (response.status === 404) {
                throw new Error('Order not found.');
            }
            if (!response.ok) {
                return response
                    .json()
                    .then((err) => {
                        const msg =
                            err.Message ??
                            err.message ??
                            'Failed to delete order.';
                        if (
                            this.gen1MenuPermTryBlock(
                                msg,
                                Constant.REPAIRREQUEST_STAT_GEN1_APPLICATION
                            )
                        ) {
                            return Promise.reject(new Error(msg));
                        }
                        throw new Error(msg);
                    })
                    .catch((parseErr) => {
                        if (parseErr instanceof Error) throw parseErr;
                        throw new Error(
                            `Order delete failed (${response.status}).`
                        );
                    });
            }
            return response.json();
        });
    },

    /** Ordered の注文についてリマインドメールを送信（MailMaster Id 14 / 送信先は既存 SendMail と同様） */
    sendGen1OrderRemindMail(csrftoken, orderId) {
        const idNum = Number(orderId);
        if (
            orderId == null ||
            orderId === '' ||
            Number.isNaN(idNum) ||
            idNum < 1
        ) {
            return Promise.reject(new Error('Invalid order id.'));
        }

        const url = `${AppConfig.ApiUrlGen1}/${idNum}/remindMail`;
        return fetch(url, {
            method: 'POST',
            mode: 'cors',
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                csrftoken: JSON.stringify(csrftoken),
            },
            body: '{}',
        }).then((response) => {
            if (response.status === 404) {
                throw new Error('Order not found.');
            }
            if (!response.ok) {
                return response
                    .json()
                    .then((err) => {
                        const msg =
                            err.Message ??
                            err.message ??
                            'Failed to send reminder email.';
                        if (
                            this.gen1MenuPermTryBlock(
                                msg,
                                Constant.REPAIRREQUEST_STAT_GEN1_HISTORY
                            )
                        ) {
                            return Promise.reject(new Error(msg));
                        }
                        throw new Error(msg);
                    })
                    .catch((parseErr) => {
                        if (parseErr instanceof Error) throw parseErr;
                        throw new Error(
                            `Reminder email failed (${response.status}).`
                        );
                    });
            }
            return response.json();
        });
    },

    downloadActivationLaborRequestSheet(csrftoken, orderId) {
        const idNum = Number(orderId);
        if (
            orderId == null ||
            orderId === '' ||
            Number.isNaN(idNum) ||
            idNum < 1
        ) {
            return Promise.reject(new Error('Invalid order id.'));
        }
        const url = `${AppConfig.ApiUrlGen1}/${idNum}/activationLaborRequestSheet`;
        return fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: { csrftoken: JSON.stringify(csrftoken) },
        }).then(async (res) => {
            if (!res.ok) {
                // PDFダウンロードの場合エラー内容はあまり返さないが一応…(JSONエラーメッセージ想定)
                let message = 'Failed to download PDF.';
                try {
                    const errJson = await res.json();
                    if (errJson && errJson.message) message = errJson.message;
                } catch {
                    // ignore parse error
                }
                throw new Error(message);
            }
            return res.blob();
        });
    },

    saveGen1Ordering(bool) {
        this.Dispatch({
            type: Constant.GEN1_ACT_SAVE_FETCHING,
            bool,
        });
    },

    saveGen1OrderingError(bool, message = '') {
        this.Dispatch({
            type: Constant.GEN1_ACT_SAVE_FETCHINGERROR,
            bool,
            message,
        });
    },

    saveGen1OrderSuccess(data, status) {
        this.Dispatch({
            type: Constant.GEN1_ACT_SAVE_SUCCESS,
            data,
            status,
        });
    },

    clearGen1OrderSaveSuccess() {
        this.Dispatch({
            type: Constant.GEN1_ACT_CLEAR_SAVE_SUCCESS,
        });
    },

    /** POST/PUT Gen1TrunkingOrder: 下書き保存・申請確定を統一 */
    saveGen1Order(csrftoken, payload, orderId, currentGen1Order) {
        const isUpdate = orderId != null && orderId !== 0 && orderId !== '';
        const url = isUpdate
            ? `${AppConfig.ApiUrlGen1}/${orderId}`
            : AppConfig.ApiUrlGen1;
        const method = isUpdate ? 'PUT' : 'POST';
        const status = payload?.Gen1TrunkingOrderHeader?.Status ?? '';

        this.saveGen1OrderingError(false);
        this.saveGen1Ordering(true);

        fetch(url, {
            mode: 'cors',
            method,
            credentials: 'include',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                csrftoken: JSON.stringify(csrftoken),
            },
            body: JSON.stringify(payload),
        })
            .then((response) => {
                if (!response.ok) {
                    return response.text().then((txt) => {
                        let msg;
                        try {
                            const err = JSON.parse(txt);
                            msg =
                                err?.Message ?? err?.message ?? 'Order failed.';
                        } catch {
                            msg =
                                txt?.trim() ||
                                `Order failed (${response.status}).`;
                        }
                        throw new Error(msg);
                    });
                }
                return response.json();
            })
            .then((data) => {
                this.saveGen1Ordering(false);
                this.saveGen1OrderSuccess(data, status);
                if (
                    data &&
                    data.id != null &&
                    data.id !== '' &&
                    data.id !== 0
                ) {
                    const baseRevision = Number(currentGen1Order?.Revision);
                    const nextRevision =
                        isUpdate &&
                        Number.isFinite(baseRevision) &&
                        baseRevision > 0
                            ? baseRevision + 1
                            : 1;
                    this.setGen1Order(
                        Object.assign({}, currentGen1Order || {}, {
                            id: data.id,
                            Revision: nextRevision,
                        })
                    );
                }
            })
            .catch((e) => {
                if (
                    !this.gen1MenuPermTryBlock(
                        e?.message,
                        Constant.REPAIRREQUEST_STAT_GEN1_APPLICATION
                    )
                ) {
                    this.saveGen1OrderingError(
                        true,
                        e?.message ?? 'An error occurred while saving.'
                    );
                }
                this.saveGen1Ordering(false);
            });
    },
};
